import React, { useMemo } from 'react';
import { Tabs } from '../../../../Components/Tabs/Tabs';
import { DateRangePicker } from '../../../../Components/DateRangePicker';
import { SearchInput } from '../../../../Components/SearchInput/SearchInput';
import { Table } from '../../../../Components/Table/Table';
import { Pagination } from '../../../../Components/Pagination/Pagination';
import { LEADERBOARD_TABS } from '../../constants';
import { LeaderboardTab } from '../../types';
import { useAdminLeaderboard } from '../../hooks/useAdminLeaderboard';
import { useExport } from '../../hooks/useExport';
import './AdminLeaderboardContent.scss';
import { AddReviewForm } from '../../../../Components/AddReviewForm/AddReviewForm';
import { AddCaseForm } from '../../../../Components/AddCaseForm/AddCaseForm';
import { ReviewsModal } from '../../../../Components/ReviewsModal/ReviewsModal';
import { CasesModal } from '../../../../Components/CasesModal/CasesModal';
import { ModalLoader } from '../../../../Components/ModalLoader';
import clsx from 'clsx';
import { StatsCards } from '../../../../Components/StatsCards/StatsCards';
import { ProgressOverview } from '../../../../Components/ProgressOverview/ProgressOverview';
import { LeaderboardEventSummary } from '../../../../api/leaderboard-events';
import useWindowDimensions from '../../../../hooks/useWindowDimensions';

interface AdminLeaderboardContentProps {
  summary: LeaderboardEventSummary | null;
}

export const AdminLeaderboardContent: React.FC<AdminLeaderboardContentProps> = ({ summary }) => {
  const { width } = useWindowDimensions();
  const { handleExport } = useExport();

  const progressData = summary
    ? [
        {
          label: 'Total Hours',
          value: summary.totalHours,
          max: summary.milestone,
        },
        {
          label: 'Total Reviews',
          value: summary.reviewsCount,
          max: summary.reviewMilestone,
        },
      ]
    : undefined;

  const {
    activeTab,
    startDate,
    endDate,
    currentPage,
    columns,
    currentData,
    totalPages,
    isLoading,
    handleTabChange,
    setStartDate,
    setEndDate,
    setCurrentPage,
    search,
    setSearch,
    isAddReviewOpen,
    closeAddReviewModal,
    selectedUserId,
    isAddCaseOpen,
    closeAddCaseModal,
    isDeleteReviewOpen,
    closeDeleteReviewModal,
    isCasesModalOpen,
    closeCasesModal,
    selectedUserName,
    selectedUserAgencyNames,
    refetchParticipants,
    activeEvent,
  } = useAdminLeaderboard();

  const selectedRow = selectedUserId
    ? currentData.find((r) => String(r.id) === selectedUserId)
    : undefined;

  const isAnyFilterActive = useMemo(
    () => Boolean(startDate || endDate || search),
    [startDate, endDate, search]
  );

  const handleClearFilters = () => {
    setStartDate(null);
    setEndDate(null);
    setSearch('');
    setCurrentPage(1);
  };

  const onExportClick = () => {
    handleExport(activeTab, activeEvent?.id, search, startDate, endDate);
  };

  return (
    <div className="leaderboard-content leaderboard-content_admin">
      <div className="leaderboard-header-top">
        <Tabs<LeaderboardTab>
          tabs={LEADERBOARD_TABS}
          activeTab={activeTab}
          onTabChange={handleTabChange}
        />
        <div className="btn outline export-button" onClick={onExportClick}>
          Export
        </div>
      </div>
      <div className="leaderboard-header-bottom">
        <div className={clsx('filter-group', { filtered: isAnyFilterActive })}>
          <DateRangePicker
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
            onClearFilters={handleClearFilters}
            minDate={summary?.eventStartDate ? new Date(summary.eventStartDate) : undefined}
            maxDate={summary?.eventEndDate ? new Date(summary.eventEndDate) : undefined}
            showClearButton={true}
          />
          <SearchInput
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search here"
          />
        </div>
      </div>

      <div className="leaderboard-table">
        {isLoading ? (
          <ModalLoader />
        ) : currentData.length === 0 ? (
          <div className="no-data-message">
            {activeTab === 'participants' && 'No participants found'}
            {activeTab === 'areas' && 'No areas found'}
            {activeTab === 'offices' && 'No offices found'}
          </div>
        ) : (
          <>
            <Table columns={columns} data={currentData} />
            {totalPages > 1 && (
              <Pagination
                totalPages={totalPages}
                currentPage={currentPage}
                onPageChange={setCurrentPage}
              />
            )}
          </>
        )}
      </div>

      {width < 768 && (
        <>
          <StatsCards summary={summary} />
          <ProgressOverview data={progressData} />
        </>
      )}

      {selectedUserId && (
        <AddReviewForm
          isOpen={isAddReviewOpen}
          onClose={closeAddReviewModal}
          onSubmit={(data) => {
            refetchParticipants();
            closeAddReviewModal();
          }}
          participantId={selectedUserId ? parseInt(selectedUserId, 10) : undefined}
          allowedOfficeNames={selectedRow?.office}
        />
      )}

      {activeEvent && (
        <AddCaseForm
          isOpen={isAddCaseOpen}
          onClose={closeAddCaseModal}
          onSubmit={(data) => {
            refetchParticipants();
            closeAddCaseModal();
          }}
          eventId={activeEvent.id}
          participantId={selectedUserId ? parseInt(selectedUserId, 10) : undefined}
          allowedAgencyNames={selectedRow?.agency || selectedUserAgencyNames || undefined}
          allowedOfficeNames={selectedRow?.office}
        />
      )}

      {isDeleteReviewOpen && selectedUserId && (
        <ReviewsModal
          isOpen={isDeleteReviewOpen}
          onClose={closeDeleteReviewModal}
          userName={selectedUserName || undefined}
          participantId={parseInt(selectedUserId, 10)}
          onChange={refetchParticipants}
        />
      )}

      {isCasesModalOpen && selectedUserId && (
        <CasesModal
          isOpen={isCasesModalOpen}
          onClose={closeCasesModal}
          userName={(selectedUserName || selectedRow?.name) as string}
          participantId={selectedUserId ? parseInt(selectedUserId, 10) : undefined}
          onChange={refetchParticipants}
        />
      )}
    </div>
  );
};
